import { ChatMessages, Features } from "@storytell/cp-client";
import type { MessageErrorData, MessageRetryData } from "@storytell/cp-client/src/gen/chat/v3/chat_messages_system_pb";
import { richTextToProseMirror } from "@storytell/rich-text";
import { produce } from "solid-js/store";
import {
  type SetStreamingStore,
  type StreamingBlockId,
  type StreamingChatId,
  type StreamingMessage,
  type StreamingMessageId,
  StreamingMessageStatus,
  type StreamingOperationId,
} from "../streamingTypes";
import type { UserId } from "@storytell/types";

export const processMessage = (setStore: SetStreamingStore, message: ChatMessages.Message) => {
  console.log("zzz processMessage", message);
};

/**
 * MESSAGE_START is the first event we receive for a message.
 */
export const processMessageStart = (
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  onChatIdCaptured?: (chatId: StreamingChatId) => void,
  onMessageStarted?: (messageId: StreamingMessageId) => void,
) => {
  if (message.data.case !== "messageStartData") {
    console.error(new Error(`Expected messageStartData, got ${message.data.case}`));
    return;
  }
  const {
    id,
    index,
    blockId,
    operationId,
    role,
    data: { value: data },
  } = message;
  const chatId = data.chatId as StreamingChatId;

  try {
    if (onChatIdCaptured) {
      onChatIdCaptured(chatId);
    }
  } catch (error) {
    console.error("Error on the callback for capturing chat ID", error);
  }

  const messageId = id as StreamingMessageId;

  try {
    if (onMessageStarted) {
      onMessageStarted(messageId);
    }
  } catch (error) {
    console.error("Error on the callback for capturing message start", error);
  }

  const streamingMessage: StreamingMessage = {
    id: messageId,
    index,
    role,
    chatId,
    operations: [],
    feature: data.feature,
    createdBy: data.userId as UserId,
    status: StreamingMessageStatus.GENERATING,
    aggregateMetrics: {
      ingressTokens: 0,
      egressTokens: 0,
      ingressBytes: 0,
      egressBytes: 0,
      totalCredits: 0,
      ingressCredits: 0,
      egressCredits: 0,
      lastUpdated: new Date(0),
    },
    prompt: {
      messageId,
      text: data.promptText,
      richText: data.promptRichText ? richTextToProseMirror(data.promptRichText) : undefined,
      blockId: blockId as StreamingBlockId,
      operationId: operationId as StreamingOperationId,
      scope: data.scope,
    },
  };

  setStore(
    produce((store) => {
      // Insert the message at the correct position
      if (!store.chats[chatId]) {
        store.chats[chatId] = {
          id: chatId,
          messages: [],
          aggregateMetrics: {
            ingressTokens: 0,
            egressTokens: 0,
            ingressBytes: 0,
            egressBytes: 0,
            totalCredits: 0,
            ingressCredits: 0,
            egressCredits: 0,
            lastUpdated: new Date(0),
          },
        };
      }

      store.messages[messageId] = streamingMessage;

      // Order the messages by oldest first
      if (store.chats[chatId] && !store.chats[chatId].messages.includes(messageId)) {
        const newMessages = [...store.chats[chatId].messages, messageId];
        store.chats[chatId].messages = newMessages.sort(
          (a, b) => (store.messages[a]?.index || 0) - (store.messages[b]?.index || 0),
        );
      }
    }),
  );
};

export const processMessageStop = (setStore: SetStreamingStore, message: ChatMessages.Message) => {
  const { id } = message;
  const messageId = id as StreamingMessageId;

  setStore(
    produce((store) => {
      const message = store.messages[messageId];
      // TODO: @andi streaming - should we throw here?
      // if (!message) {
      //   throw new Error(`Message not found for stop: ${messageId}`);
      // }
      if (message) {
        message.status = StreamingMessageStatus.COMPLETED;
      }
    }),
  );
};

export const processMessageError = (setStore: SetStreamingStore, message: ChatMessages.Message) => {
  if (message.data.case !== "messageErrorData") {
    console.error(new Error(`Expected messageErrorData, got ${message.data.case}`));
    return;
  }

  const { id, operationId: messageOperationId, index: _index } = message;
  const originalMessageId = id as StreamingMessageId;
  const operationId = messageOperationId as StreamingOperationId;

  setStore(
    produce((store) => {
      const originalMessage = store.messages[originalMessageId];
      const chatId = originalMessage?.chatId;
      if (!chatId) {
        console.error(new Error(`Chat not found for message error: ${originalMessageId}`));
        return;
      }

      // Find existing operation (don't create new one)
      const existingOperation = store.operations[operationId];
      if (!existingOperation) {
        console.error(new Error(`Operation ${operationId} not found for error message`));
        return;
      }

      // Create block ID for error message
      const blockId = `block_${originalMessageId}_error_${Date.now()}` as StreamingBlockId;

      // Update existing operation status to FAILED (stops spinner)
      existingOperation.status = ChatMessages.OperationStatus.FAILED;
      existingOperation.isComplete = true;
      existingOperation.completedAt = new Date();

      const data = message.data.value as MessageErrorData;
      const streamingBlock = {
        type: "block" as const,
        isComplete: true,
        blockId,
        operationId,
        messageId: originalMessageId,
        role: ChatMessages.Role.SYSTEM,
        activity: "error",
        feature: originalMessage?.feature ?? (Features.Feature.UNSPECIFIED as unknown as Features.Feature),
        messageType: ChatMessages.MessageType.INTERSTITIAL_USER_FACING,
        title: "Error",
        deltas: [],
        finalContent: {
          type: "error" as const,
          displayMessage: data?.displayMessage || "An error occurred",
          systemDetail: data?.systemDetail,
          reason: data?.reason,
        },
        searchResults: undefined,
        webSearchResults: undefined,
        conceptResults: undefined,
        structuredResults: undefined,
        imageResults: undefined,
        metrics: undefined,
      };

      // Create chat if missing
      if (!store.chats[chatId]) {
        store.chats[chatId] = {
          id: chatId,
          messages: [],
          aggregateMetrics: {
            ingressTokens: 0,
            egressTokens: 0,
            ingressBytes: 0,
            egressBytes: 0,
            totalCredits: 0,
            ingressCredits: 0,
            egressCredits: 0,
            lastUpdated: new Date(0),
          },
        };
      }

      // Add block to existing operation
      existingOperation.blocks.push(blockId);

      // Persist block
      store.blocks[blockId] = streamingBlock;

      // Mark message as failed
      if (originalMessage) {
        originalMessage.status = StreamingMessageStatus.FAILED;
      }
    }),
  );
};

export const processMessageRetry = (setStore: SetStreamingStore, message: ChatMessages.Message) => {
  if (message.data.case !== "messageRetryData") {
    console.error(new Error(`Expected messageRetryData, got ${message.data.case}`));
    return;
  }

  const { id, operationId: messageOperationId, index: _index } = message;
  const originalMessageId = id as StreamingMessageId;
  const operationId = messageOperationId as StreamingOperationId;

  setStore(
    produce((store) => {
      const originalMessage = store.messages[originalMessageId];
      const chatId = originalMessage?.chatId;
      if (!chatId) {
        console.error(new Error(`Chat not found for message retry: ${originalMessageId}`));
        return;
      }

      // Find existing operation (don't create new one)
      const existingOperation = store.operations[operationId];
      if (!existingOperation) {
        console.error(new Error(`Operation ${operationId} not found for retry message`));
        return;
      }

      // Create block ID for retry message
      const blockId = `block_${originalMessageId}_retry_${Date.now()}` as StreamingBlockId;

      // Update existing operation status to RETRYING
      existingOperation.status = ChatMessages.OperationStatus.RETRYING;

      const data = message.data.value as MessageRetryData;
      const streamingBlock = {
        type: "block" as const,
        isComplete: true,
        blockId,
        operationId,
        messageId: originalMessageId,
        role: ChatMessages.Role.SYSTEM,
        activity: "retry",
        feature: originalMessage?.feature ?? (Features.Feature.UNSPECIFIED as unknown as Features.Feature),
        messageType: ChatMessages.MessageType.INTERSTITIAL_USER_FACING,
        title: "Refining results",
        deltas: [],
        finalContent: {
          type: "retry" as const,
          message: data?.message || "Retrying with adjusted parameters...",
          // Don't expose technical details to UI
        },
        searchResults: undefined,
        webSearchResults: undefined,
        conceptResults: undefined,
        structuredResults: undefined,
        imageResults: undefined,
        metrics: undefined,
      };

      // Create chat if missing
      if (!store.chats[chatId]) {
        store.chats[chatId] = {
          id: chatId,
          messages: [],
          aggregateMetrics: {
            ingressTokens: 0,
            egressTokens: 0,
            ingressBytes: 0,
            egressBytes: 0,
            totalCredits: 0,
            ingressCredits: 0,
            egressCredits: 0,
            lastUpdated: new Date(0),
          },
        };
      }

      // Add block to existing operation
      existingOperation.blocks.push(blockId);

      // Persist block
      store.blocks[blockId] = streamingBlock;
    }),
  );
};
